/**
 * Error Logging Service
 * Captures frontend errors and sends them to the backend for tracking
 */

import { backendApiService } from './backendApiService';

interface ErrorLogData {
  error_type: 'js_error' | 'api_error' | 'network' | 'timeout' | 'unhandled_rejection';
  error_code?: string;
  error_message: string;
  error_stack?: string;
  user_email?: string;
  page_url?: string;
  api_endpoint?: string;
  request_method?: string;
  request_body?: Record<string, unknown>;
  severity?: 'error' | 'warning' | 'critical';
  component?: string;
  additional_context?: Record<string, unknown>;
}

class ErrorLoggingService {
  private static instance: ErrorLoggingService;
  private userEmail: string | null = null;
  private isInitialized = false;
  private errorQueue: ErrorLogData[] = [];
  private isProcessingQueue = false;

  private constructor() {}

  static getInstance(): ErrorLoggingService {
    if (!ErrorLoggingService.instance) {
      ErrorLoggingService.instance = new ErrorLoggingService();
    }
    return ErrorLoggingService.instance;
  }

  /**
   * Initialize the error logging service
   * Call this once on app startup
   */
  initialize(userEmail?: string): void {
    if (this.isInitialized) return;

    this.userEmail = userEmail || null;

    // Listen for unhandled errors
    window.addEventListener('error', this.handleGlobalError.bind(this));

    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));

    this.isInitialized = true;
    console.log('[ErrorLogging] Initialized');

    // Process any queued errors
    this.processQueue();
  }

  /**
   * Set the current user email
   */
  setUserEmail(email: string | null): void {
    this.userEmail = email;
  }

  /**
   * Get browser and device info
   */
  private getEnvironmentInfo() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let browserVersion = '';
    let os = 'Unknown';
    let deviceType = 'desktop';

    // Detect browser
    if (ua.includes('Firefox')) {
      browser = 'Firefox';
      browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Chrome')) {
      browser = 'Chrome';
      browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Safari')) {
      browser = 'Safari';
      browserVersion = ua.match(/Version\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Edge')) {
      browser = 'Edge';
      browserVersion = ua.match(/Edge\/(\d+)/)?.[1] || '';
    }

    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    // Detect device type
    if (ua.includes('Mobile') || ua.includes('Android')) deviceType = 'mobile';
    else if (ua.includes('Tablet') || ua.includes('iPad')) deviceType = 'tablet';

    return {
      browser,
      browser_version: browserVersion,
      os,
      device_type: deviceType,
      screen_resolution: `${window.screen.width}x${window.screen.height}`
    };
  }

  /**
   * Handle global JavaScript errors
   */
  private handleGlobalError(event: ErrorEvent): void {
    this.logError({
      error_type: 'js_error',
      error_message: event.message,
      error_stack: event.error?.stack,
      page_url: window.location.href,
      severity: 'error',
      additional_context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  }

  /**
   * Handle unhandled promise rejections
   */
  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = event.reason;
    this.logError({
      error_type: 'unhandled_rejection',
      error_message: error?.message || String(error),
      error_stack: error?.stack,
      page_url: window.location.href,
      severity: 'error'
    });
  }

  /**
   * Log an API error
   */
  logApiError(
    endpoint: string,
    method: string,
    statusCode: number | string,
    errorMessage: string,
    requestBody?: Record<string, unknown>,
    additionalContext?: Record<string, unknown>
  ): void {
    // Determine severity based on status code
    let severity: 'error' | 'warning' | 'critical' = 'error';
    if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
      severity = 'critical';
    } else if (statusCode === 429 || statusCode === 408) {
      severity = 'warning';
    }

    this.logError({
      error_type: 'api_error',
      error_code: String(statusCode),
      error_message: errorMessage,
      api_endpoint: endpoint,
      request_method: method,
      request_body: requestBody,
      page_url: window.location.href,
      severity,
      additional_context: additionalContext
    });
  }

  /**
   * Log a network error
   */
  logNetworkError(endpoint: string, errorMessage: string): void {
    this.logError({
      error_type: 'network',
      error_message: errorMessage,
      api_endpoint: endpoint,
      page_url: window.location.href,
      severity: 'error'
    });
  }

  /**
   * Log a timeout error
   */
  logTimeoutError(endpoint: string, timeoutMs: number): void {
    this.logError({
      error_type: 'timeout',
      error_message: `Request timed out after ${timeoutMs}ms`,
      api_endpoint: endpoint,
      page_url: window.location.href,
      severity: 'warning'
    });
  }

  /**
   * Log a custom error
   */
  logCustomError(
    message: string,
    component: string,
    severity: 'error' | 'warning' | 'critical' = 'error',
    additionalContext?: Record<string, unknown>
  ): void {
    this.logError({
      error_type: 'js_error',
      error_message: message,
      page_url: window.location.href,
      component,
      severity,
      additional_context: additionalContext
    });
  }

  /**
   * Main error logging function
   */
  private async logError(data: ErrorLogData): Promise<void> {
    const env = this.getEnvironmentInfo();

    const errorData = {
      ...data,
      ...env,
      user_email: this.userEmail || undefined
    };

    // Add to queue
    this.errorQueue.push(errorData);

    // Process queue
    this.processQueue();
  }

  /**
   * Process the error queue (debounced)
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.errorQueue.length === 0) return;

    this.isProcessingQueue = true;

    // Small delay to batch errors
    await new Promise(resolve => setTimeout(resolve, 100));

    while (this.errorQueue.length > 0) {
      const error = this.errorQueue.shift();
      if (error) {
        try {
          await backendApiService.post('/api/errors/log', error);
        } catch (err) {
          // Silently fail - don't create infinite error loops
          console.error('[ErrorLogging] Failed to send error to server:', err);
        }
      }
    }

    this.isProcessingQueue = false;
  }
}

export const errorLoggingService = ErrorLoggingService.getInstance();
